/* oxlint-disable max-lines-per-function */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCurrentUser, useTargetBrandConfig, useOfflineModeManager, useNavigationHelper } from 'FrontRoyalAngular';
import { hasSuperEditorAccess } from 'Users';
import { useAngularContext } from 'AngularContext';
import { getRouteMetadata } from 'FrontRoyalRouteRegistry';
import { popLearningBreadcrumbs } from 'StreamDashboard/popLearningBreadcrumbs';
import { animatePathChange } from 'RouteAnimation';
import {
    appHeaderActions,
    selectAppHeaderHidden,
    selectAppHeaderHasTopMessage,
    selectAppHeaderShowCaret,
    selectAppHeaderShowAlternateHomeButton,
    selectAppHeaderTitle,
    selectAppHeaderTitleOpts,
    selectAppHeaderViewedStream,
    selectAppHeaderMobileKeyboardShowing,
    selectAppHeaderPlayerViewModel,
} from '../redux';
import { type HeaderLayout, type TitleOpts, type ViewedStream } from '../types';
import { useActiveTab } from './useActiveTab';
import { useRouteInfo } from './useRouteInfo';

// Extended brand config type for properties not in TypeScript type
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type ExtendedBrandConfig = any;

/**
 * Hook that provides access to app header state and actions.
 *
 * This hook combines:
 * - Redux state (mutable state like hidden, title, viewedStream)
 * - Derived state (layout, hideMobileMenu, computed from route + Redux)
 * - External data (brand config, current user, navigation helpers)
 * - Action dispatchers for updating Redux state
 */
export function useAppHeader() {
    const dispatch = useDispatch();
    const $injector = useAngularContext();
    const vanillaUser = useCurrentUser();
    const targetBrandConfig = useTargetBrandConfig() as ExtendedBrandConfig;
    const offlineModeManager = useOfflineModeManager();
    const { loadRoute, goBack } = useNavigationHelper();
    const { currentPath, isActive } = useRouteInfo();

    // Track previous path for mobile menu back navigation
    const previousPathRef = useRef<string | null>(null);
    useEffect(
        () => () => {
            previousPathRef.current = currentPath;
        },
        [currentPath],
    );

    // Redux state
    const hidden = useSelector(selectAppHeaderHidden);
    const hasTopMessage = useSelector(selectAppHeaderHasTopMessage);
    const showCaret = useSelector(selectAppHeaderShowCaret);
    const showAlternateHomeButton = useSelector(selectAppHeaderShowAlternateHomeButton);
    const title = useSelector(selectAppHeaderTitle);
    const titleOpts = useSelector(selectAppHeaderTitleOpts);
    const viewedStream = useSelector(selectAppHeaderViewedStream);
    const mobileKeyboardShowing = useSelector(selectAppHeaderMobileKeyboardShowing);
    const playerViewModel = useSelector(selectAppHeaderPlayerViewModel);

    // Active tab state (derived from current path)
    const activeTab = useActiveTab(currentPath);

    // Computed: layout from route metadata
    const layout = useMemo<HeaderLayout>(() => {
        const metadata = getRouteMetadata(currentPath);
        const headerLayout = metadata?.headerLayout;
        if (!headerLayout || headerLayout === 'default') return 'default';
        return headerLayout as HeaderLayout;
    }, [currentPath]);

    // Computed: hideMobileMenu
    const hideMobileMenu = useMemo(() => {
        if (playerViewModel) return true; // Hide mobile menu when in lesson player
        if (mobileKeyboardShowing) return true;
        const metadata = getRouteMetadata(currentPath);
        if (metadata?.removeAppShell === true) return true;
        return false;
    }, [currentPath, mobileKeyboardShowing, playerViewModel]);

    // Computed: allowTapScroll (static, based on device capabilities)
    const allowTapScroll = useMemo(() => typeof window !== 'undefined' && 'ontouchstart' in window, []);

    // Cordova check
    const isCordova = typeof window !== 'undefined' && !!(window as unknown as { CORDOVA?: boolean }).CORDOVA;

    // Computed: shouldShowTopMessage
    const shouldShowTopMessage = useMemo(() => hasTopMessage && isCordova, [hasTopMessage, isCordova]);

    // Computed: appHeaderClasses
    const appHeaderClasses = useMemo(() => {
        const classes = ['app-header', layout, ...(targetBrandConfig?.appHeaderStyleClasses ?? [])];
        if (hasTopMessage) {
            classes.push('has-top-message');
        }
        return classes;
    }, [layout, targetBrandConfig?.appHeaderStyleClasses, hasTopMessage]);

    // Computed: logoAlt
    const logoAlt = useMemo(
        () => targetBrandConfig?.logoAlt ?? targetBrandConfig?.brandNameShort ?? 'Logo',
        [targetBrandConfig?.logoAlt, targetBrandConfig?.brandNameShort],
    );

    // Action: goHome
    const goHome = useCallback(() => {
        const reactRouterBridge = $injector.get<{ url: () => string }>('reactRouterBridge');
        const currentUrl = reactRouterBridge.url();
        if (currentUrl !== '/' && currentUrl !== '/dashboard') {
            loadRoute('/dashboard');
        }
    }, [$injector, loadRoute]);

    // Action: followLearningBreadcrumbsBack
    const followLearningBreadcrumbsBack = useCallback(() => {
        const path = popLearningBreadcrumbs(currentPath);
        animatePathChange(path, 'backward');
    }, [currentPath]);

    // Action: toggleVisibility
    const toggleVisibility = useCallback(
        (visible: boolean) => {
            dispatch(appHeaderActions.toggleVisibility(visible));
            const body = document.body;
            if (!visible) {
                body.classList.add('header-invisible');
            } else {
                body.classList.remove('header-invisible');
            }
        },
        [dispatch],
    );

    // Action: scrollToTop
    const scrollToTop = useCallback((smooth: boolean) => {
        const scrollOptions: ScrollToOptions = { top: 0 };
        if (smooth) {
            scrollOptions.behavior = 'smooth';
        }
        const mainContainer = document.getElementById('app-main-container');
        if (mainContainer) {
            mainContainer.scrollTo(scrollOptions);
        } else {
            window.scrollTo(scrollOptions);
        }
    }, []);

    // Action: setTitleHTML
    const setTitleHTML = useCallback(
        (titleHTML?: string, opts: TitleOpts = {}) => {
            dispatch(appHeaderActions.setTitleHTML({ title: titleHTML, opts }));
        },
        [dispatch],
    );

    // Action: setShowAlternateHomeButton
    const setShowAlternateHomeButton = useCallback(
        (show: boolean) => {
            dispatch(appHeaderActions.setShowAlternateHomeButton(show));
        },
        [dispatch],
    );

    // Action: setHasTopMessage
    const setHasTopMessage = useCallback(
        (hasMessage: boolean) => {
            dispatch(appHeaderActions.setHasTopMessage(hasMessage));
        },
        [dispatch],
    );

    // Action: setShowCaret
    const setShowCaret = useCallback(
        (show: boolean) => {
            dispatch(appHeaderActions.setShowCaret(show));
        },
        [dispatch],
    );

    // Action: setViewedStream
    const setViewedStream = useCallback(
        (stream: ViewedStream | null) => {
            dispatch(appHeaderActions.setViewedStream(stream));
        },
        [dispatch],
    );

    // Action: setBodyBackground
    const setBodyBackground = useCallback(
        (color?: string) => {
            color = color || 'slate-grey-100-alt';
            const body = document.body;
            const validColors = [
                'blue',
                'purple',
                'coral',
                'turquoise',
                'landing',
                'completion-blue',
                'white',
                'beige',
                'beige-pattern',
                'demo-pattern',
                'slate-grey-100-alt',
            ];

            if (!validColors.includes(color)) {
                console.error(`unsupported background color "${color}"`);
                return;
            }

            // Remove existing bg- classes
            validColors.forEach(c => {
                body.classList.remove(`bg-${c}`);
            });

            // Add the new one
            body.classList.add(`bg-${color}`);

            // Add brand class
            if (targetBrandConfig?.brandStyleClass) {
                body.classList.add(targetBrandConfig.brandStyleClass);
            }
        },
        [targetBrandConfig?.brandStyleClass],
    );

    // Action: handleMobileSettingsMenuClick
    const handleMobileSettingsMenuClick = useCallback(() => {
        if (currentPath?.startsWith('/settings')) {
            // If the previous route was also a settings route, go to dashboard
            // Otherwise go back to where they came from
            if (previousPathRef.current?.startsWith('/settings')) {
                loadRoute('/dashboard');
            } else {
                goBack();
            }
        } else if (!offlineModeManager?.inOfflineMode) {
            loadRoute('/settings/mobile');
        }
    }, [currentPath, goBack, loadRoute, offlineModeManager?.inOfflineMode]);

    // Action: shareStream
    const shareStream = useCallback(() => {
        if (!isCordova || !viewedStream) return;
        const ShareService = $injector.get<{
            shareMobileApp: (info: { title: string; url: string }) => void;
        }>('Navigation.ShareService');
        if (ShareService && viewedStream) {
            ShareService.shareMobileApp({
                title: viewedStream.title,
                url: window.location.href,
            });
        }
    }, [isCordova, viewedStream, $injector]);

    // Action: toggleBookmark
    const toggleBookmark = useCallback(
        (stream: ViewedStream) => {
            if (vanillaUser) {
                const $rootScope = $injector.get<{ currentUser?: { toggleBookmark: (stream: ViewedStream) => void } }>(
                    '$rootScope',
                );
                $rootScope.currentUser?.toggleBookmark(stream);
            }
        },
        [vanillaUser, $injector],
    );

    // Action: exitButtonClick - Exit from lesson player
    const exitButtonClick = useCallback(() => {
        if (!playerViewModel) return;

        // Preview mode - don't allow exit
        if (playerViewModel.previewMode) return;

        // Demo mode - send message to parent window
        if (playerViewModel.demoMode) {
            window.parent.postMessage('closeDemo', '*');
            return;
        }

        // Normal exit - navigate to stream dashboard
        if (playerViewModel.streamDashboardPath) {
            setBodyBackground();
            loadRoute(playerViewModel.streamDashboardPath, { replace: true });
            return;
        }

        // Fallback - go back
        goBack();
    }, [playerViewModel, setBodyBackground, loadRoute, goBack]);

    // Action: backButtonClick - Go to previous frame in lesson player
    const backButtonClick = useCallback(() => {
        if (!playerViewModel) return;

        // Delegate to Angular AppHeaderViewModel for gotoPrev functionality
        const AppHeaderViewModel = $injector.get<{
            playerViewModel?: { log: (event: string) => void; gotoPrev: () => void };
        }>('Navigation.AppHeader.AppHeaderViewModel');

        if (AppHeaderViewModel.playerViewModel) {
            AppHeaderViewModel.playerViewModel.log('lesson:back-button-click');
            AppHeaderViewModel.playerViewModel.gotoPrev();
            (document.activeElement as HTMLElement | null)?.blur();
        }
    }, [playerViewModel, $injector]);

    // Language switcher state
    const [showLanguageSwitcher, setShowLanguageSwitcher] = useState(false);

    // Action: toggleLanguageSwitcher - Toggle language switcher visibility
    const toggleLanguageSwitcher = useCallback(
        (val?: boolean) => {
            // Get playerViewModel from Angular to check permissions
            const AppHeaderViewModel = $injector.get<{
                playerViewModel?: {
                    lesson?: { locale: string };
                    editorMode?: boolean;
                    previewMode?: boolean;
                };
            }>('Navigation.AppHeader.AppHeaderViewModel');
            const pvm = AppHeaderViewModel.playerViewModel;

            // Check if language switcher can be shown
            const canShow =
                (vanillaUser ? hasSuperEditorAccess(vanillaUser) : false) &&
                pvm &&
                pvm.lesson?.locale !== 'en' &&
                !pvm.editorMode &&
                !pvm.previewMode;

            if (!canShow) {
                setShowLanguageSwitcher(false);
            } else {
                setShowLanguageSwitcher(val ?? !showLanguageSwitcher);
            }
        },
        [vanillaUser, $injector, showLanguageSwitcher],
    );

    // Action: swapLanguage - Swap between lesson language and English
    const swapLanguage = useCallback(() => {
        const AppHeaderViewModel = $injector.get<{
            playerViewModel?: {
                swapLanguage?: () => void;
            };
        }>('Navigation.AppHeader.AppHeaderViewModel');
        const pvm = AppHeaderViewModel.playerViewModel;
        if (pvm?.swapLanguage) {
            pvm.swapLanguage();
        }
    }, [$injector]);

    return {
        // Redux state
        hidden,
        hasTopMessage,
        showCaret,
        showAlternateHomeButton,
        title,
        titleOpts,
        viewedStream,
        playerViewModel,

        // Derived state
        layout,
        hideMobileMenu,
        activeTab,
        allowTapScroll,

        // Computed values
        appHeaderClasses,
        logoAlt,
        shouldShowTopMessage,

        // Current user
        currentUser: vanillaUser,

        // Brand configuration
        brandStyleClass: targetBrandConfig?.brandStyleClass ?? '',
        appHeaderStyleClasses: targetBrandConfig?.appHeaderStyleClasses ?? [],
        appHeaderLearnerLayoutLogo: targetBrandConfig?.appHeaderV2LearnerLayoutLogo ?? null,
        appHeaderWordMarkSrc: targetBrandConfig?.appHeaderV2WordMarkSrc ?? '',
        appHeaderWordMarkStyles: targetBrandConfig?.appHeaderWordMarkStyles ?? {},
        appMenuMobileLogo: targetBrandConfig?.appMenuMobileLogo ?? '',
        brandNameShort: targetBrandConfig?.brandNameShort ?? '',
        brandNameAbbr: targetBrandConfig?.brandNameAbbr ?? '',

        // Environment
        inOfflineMode: offlineModeManager?.inOfflineMode ?? false,
        isCordova,

        // Navigation
        loadRoute,
        goBack,
        isActive,
        currentPath,

        // Actions
        goHome,
        followLearningBreadcrumbsBack,
        toggleVisibility,
        scrollToTop,
        setTitleHTML,
        setShowAlternateHomeButton,
        setHasTopMessage,
        setShowCaret,
        setViewedStream,
        setBodyBackground,
        handleMobileSettingsMenuClick,
        shareStream,
        toggleBookmark,
        exitButtonClick,
        backButtonClick,

        // Language switcher
        showLanguageSwitcher,
        toggleLanguageSwitcher,
        swapLanguage,
    };
}
